import type React from 'react';

/**
 * Fade-in for a listing card's picture: spread one of these onto an <img> or a poster <video> whose styled component
 * includes the `fadeInWhenReady` mixin (theme/mixins). The element is held hidden (`data-reveal`) until its pixels are
 * ready, then marked `data-ready` and faded in, so the picture arrives on the trigger that matters (the image landing)
 * rather than popping in under the card's own entrance. The mark is a DOM attribute React does not own, so the props
 * work inside a render callback where a hook cannot be called. Key the element by its address where it is used, so a
 * new picture starts hidden again.
 */

const markReady = (el: HTMLElement): void => {
  el.setAttribute('data-ready', '');
};

/** Whether a video has a duration to seek within (the library seeks a poster to a representative frame). */
const seekable = (video: HTMLVideoElement): boolean => Number.isFinite(video.duration) && video.duration > 0;

/** For an <img>: ready once loaded and decoded (a failed one is released too, showing the frame's own tint). An image
 *  already in the cache is ready from its first paint, with no fade. */
export const revealOnLoad = {
  'data-reveal': '',
  ref: (img: HTMLImageElement | null): void => {
    if (img?.complete) markReady(img);
  },
  onLoad: (event: React.SyntheticEvent<HTMLImageElement>): void => {
    const img = event.currentTarget;
    // Decoded, not just fetched: a large image that shows half-painted would pop all the same.
    img.decode().then(
      () => markReady(img),
      () => markReady(img),
    );
  },
  onError: (event: React.SyntheticEvent<HTMLImageElement>): void => markReady(event.currentTarget),
};

/** For a poster <video>: ready on the frame it shows, the representative frame once it has seeked there, or its first
 *  frame when it has no duration to seek within. A failed one is released too. */
export const revealOnFrame = {
  'data-reveal': '',
  onSeeked: (event: React.SyntheticEvent<HTMLVideoElement>): void => markReady(event.currentTarget),
  onLoadedData: (event: React.SyntheticEvent<HTMLVideoElement>): void => {
    if (!seekable(event.currentTarget)) markReady(event.currentTarget);
  },
  onError: (event: React.SyntheticEvent<HTMLVideoElement>): void => markReady(event.currentTarget),
};
